import React, { FC, memo, useCallback, useEffect, useState, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Link } from 'react-router-dom';
import { getGlobalSettings } from 'app/appState';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Modal } from 'shared/ui/Modal';
import { Checkbox } from 'shared/ui/Checkbox';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { generateContractPdfLink, useUpdateContractMutation } from 'entities/Contract';
import { ReactComponent as ContractWhiteIcon } from 'shared/assets/icons/ContractWhiteIcon.svg';
import { Button } from 'shared/ui/Button';
import { BookingDocumentsNames } from 'app/appState/model/types';
import { contractAgreementModalActions } from '../model/slice/contractAgreementModalSlice';
import { getContractAgreementState } from '../model/selectors/getContractAgreementState';

export const ContractAgreementModal: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const dispatch = useAppDispatch();
  const { contract } = useAppSelector(getContractAgreementState);
  const globalSettings = useAppSelector(getGlobalSettings);

  const [isOpened, setOpened] = useState(false);
  const [isUserAgreedWithContract, setUserAgreementWithContract] = useState(false);

  const { isDesktop } = useScreenBreakpoints();
  const [updateContract] = useUpdateContractMutation();

  const openModal = useCallback(() => {
    setOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpened(false);
    dispatch(contractAgreementModalActions.clearContractAgreementModal());
    setUserAgreementWithContract(false);
  }, []);

  useEffect(() => {
    contract && openModal();
  }, [contract, openModal]);

  const privacyPolicyLink = useMemo(
    () => globalSettings?.bookingDocuments.find((el) => el.docName === BookingDocumentsNames.PRIVACY_POLICY)?.link || '#',
    [globalSettings],
  );

  const agreeWithContract = useCallback(async (): Promise<void> => {
    try {
      if (contract) {
        await updateContract({ contractId: contract.contractId, params: { isContractSigned: true } }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Contract has been successfully agreed', { ns: 'contracts' }));
        closeModal();
      }
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when setting agreement on contract', { ns: 'contracts' }));
    }
  }, [contract, updateContract, t, closeModal]);

  if (!contract) {
    return null;
  }

  return (
    <div className="mt-2 mb-6">
      <Modal isOpen={isOpened} onClose={closeModal} width={isDesktop ? 520 : undefined}>
        <div className="space-y-2.5">
          <div className="font-semibold text-lg">{t('Please, agree with the following agreement:')}</div>
          <div className="text-primaryLight font-normal">
            {t('To get access to the box, please, agree with contract and privacy policy:')}
          </div>

          <div className="flex flex-col">
            <Link className="text-accent text-md font-normal" to={generateContractPdfLink(contract?.contractId)} target="_blank">
              - {t('Contract')} #{contract.contractNumber}
            </Link>
            <Link className="text-accent text-md font-normal" to={privacyPolicyLink} target="_blank">
              {t(BookingDocumentsNames.PRIVACY_POLICY, { ns: 'common' })}
            </Link>
          </div>
          <Checkbox className="my-2" name="termsAgreement" checked={isUserAgreedWithContract} onChange={setUserAgreementWithContract}>
            {t('I agree with contract and privacy policy')}
          </Checkbox>
          <Button isDisabled={!isUserAgreedWithContract} icon={<ContractWhiteIcon />} iconPosition="prev" onClick={agreeWithContract}>
            {t('Confirm', { ns: 'common' })}
          </Button>
        </div>
      </Modal>
    </div>
  );
});
